import * as RadixPopover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronDown, Plus } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Icon } from "../icons/Icon";
import { cx, DISABLED, FOCUS_RING, TOUCH_TARGET } from "../styles";
import { SURFACE } from "./Menu";
import { useUiText } from "./uiText";

/**
 * A select: a button that opens a searchable list, rather than `<select>`.
 *
 * The native element cannot be styled consistently across browsers — the
 * dropdown is drawn by the operating system — and on Windows it renders a list
 * that ignores the page's theme entirely, which in dark mode is a white
 * rectangle. cmdk gives the list its keyboard behaviour: arrows, Enter, and
 * Escape with focus returned to the trigger.
 *
 * It is not a form control, so a hidden input carries `name` for a `<form>`.
 *
 * - **Every list with options has a search field** (owner, 2026-10-22: "all
 *   dropdown lists", after ADJ-1022-1's bar of eight left a warehouse's few
 *   custom roles unsearchable), and draws at most {@link SELECT_RENDER_CAP}
 *   rows at a time: a list of two thousand stores mounted two thousand DOM
 *   rows on every open, which is a visible freeze on a warehouse laptop, and
 *   nobody scrolls two thousand rows anyway.
 * - **No options** opens a short explanation (`emptyLabel`) and, when the
 *   caller gives one, an `emptyAction` row — "Create a category" — rather than
 *   an empty list under "Choose one" that looks broken.
 *
 * Both are one component, so a list opened while its options are still
 * loading stays open when they arrive (two components swapped on arrival, and
 * the remount shut it).
 */

/** Above this many options the list gets a search field: every non-empty list. */
export const SEARCHABLE_SELECT_THRESHOLD = 0;

/** The most rows a list draws at once; typing narrows the rest. */
export const SELECT_RENDER_CAP = 100;

export interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean | undefined;
}

export interface SelectProps {
	options: SelectOption[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	/** Shown when nothing is chosen. */
	placeholder?: string;
	disabled?: boolean;
	name?: string;
	id?: string;
	"aria-label"?: string | undefined;
	// Explicitly `| undefined`: these come straight from `Field`, which passes
	// `undefined` for a field with no hint and no error — a state, not a prop
	// somebody forgot.
	"aria-describedby"?: string | undefined;
	"aria-invalid"?: true | undefined;
	className?: string | undefined;
	/** Why there is nothing to choose, when `options` is empty. */
	emptyLabel?: string | undefined;
	/**
	 * What to do about it, as a row of the list under `emptyLabel` — a row
	 * rather than a free-form node so the keyboard reaches it like any option.
	 */
	emptyAction?: { label: string; onSelect: () => void } | undefined;
	/** Kept for the callers that ask for it; every non-empty list searches now. */
	searchable?: boolean | undefined;
}

/** The value the empty-state action row carries; never reported as a choice. */
const EMPTY_ACTION = "__empty_action__";

/** The closed control. */
const TRIGGER = cx(
	"flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border-strong",
	"bg-bg-surface px-2.5 text-[13px] text-fg cursor-pointer shadow-xs",
	"hover:border-fg-subtle data-[placeholder]:text-fg-subtle",
	"aria-[invalid]:border-danger-border aria-[invalid]:ring-3 aria-[invalid]:ring-danger-bg",
	FOCUS_RING,
	TOUCH_TARGET,
	DISABLED,
);

const ITEM = cx(
	"flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5",
	"text-[13px] text-fg outline-none",
	"max-sm:min-h-12",
);

function Chevron() {
	// Not mirrored: the chevron points down, and down is down in every direction.
	return <Icon as={ChevronDown} size={16} className="shrink-0 text-fg-subtle" />;
}

export function Select({
	options,
	value,
	defaultValue,
	onValueChange,
	placeholder,
	disabled,
	name,
	id,
	className,
	"aria-label": ariaLabel,
	"aria-describedby": describedBy,
	"aria-invalid": invalid,
	emptyLabel,
	emptyAction,
}: SelectProps) {
	const t = useUiText();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [uncontrolled, setUncontrolled] = useState(defaultValue);
	const input = useRef<HTMLInputElement>(null);
	const current = value ?? uncontrolled;
	const chosen = options.find((option) => option.value === current);
	const empty = options.length === 0;

	const matches = useMemo(() => matchOptions(options, query), [options, query]);
	const shown = matches.slice(0, SELECT_RENDER_CAP);

	return (
		<RadixPopover.Root
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) {
					setQuery("");
				}
			}}
		>
			<RadixPopover.Trigger asChild>
				<button
					type="button"
					role="combobox"
					aria-expanded={open}
					id={id}
					disabled={disabled}
					aria-label={ariaLabel}
					aria-describedby={describedBy}
					aria-invalid={invalid}
					{...(chosen === undefined ? { "data-placeholder": "" } : {})}
					className={cx(TRIGGER, className)}
				>
					{/* One line, cut with an ellipsis: a long value wrapped inside a
					    36px trigger spilled out of it on a phone (ADJ-M01-1). */}
					<span className="min-w-0 truncate text-start">{chosen?.label ?? placeholder}</span>
					<Chevron />
				</button>
			</RadixPopover.Trigger>
			{name !== undefined && <input type="hidden" name={name} value={current ?? ""} />}
			<RadixPopover.Portal>
				<RadixPopover.Content
					align="start"
					sideOffset={4}
					collisionPadding={8}
					className={cx(SURFACE, "w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden")}
					onOpenAutoFocus={(event) => {
						event.preventDefault();
						input.current?.focus();
					}}
				>
					<Command shouldFilter={false} label={ariaLabel ?? placeholder ?? ""}>
						{/* A search field on any list with something to search. */}
						{!empty && (
							<div className="border-b border-border p-2">
								<Command.Input
									ref={input}
									value={query}
									onValueChange={setQuery}
									placeholder={t("ui.actions.search")}
									className="h-8 w-full bg-transparent px-1 text-[13px] text-fg outline-none placeholder:text-fg-subtle"
								/>
							</div>
						)}
						<Command.List className="max-h-64 overflow-y-auto p-1" data-scroll="1">
							{empty && (
								<p className="max-w-72 px-2 py-2 text-[12.5px] leading-relaxed text-fg-muted">
									{emptyLabel ?? t("ui.select.empty")}
								</p>
							)}
							{empty && emptyAction !== undefined && (
								<Command.Item
									value={EMPTY_ACTION}
									onSelect={() => {
										setOpen(false);
										emptyAction.onSelect();
									}}
									className={cx(
										ITEM,
										"font-semibold text-brand-ink data-[selected=true]:bg-brand-subtle",
									)}
								>
									<Icon as={Plus} size={14} />
									<span>{emptyAction.label}</span>
								</Command.Item>
							)}
							{!empty && (
								<Command.Empty className="p-3 text-[12px] text-fg-subtle">
									{t("ui.select.noMatches")}
								</Command.Empty>
							)}
							{shown.map((option) => (
								<Command.Item
									key={option.value}
									value={option.value}
									{...(option.disabled === undefined ? {} : { disabled: option.disabled })}
									onSelect={() => {
										setUncontrolled(option.value);
										onValueChange?.(option.value);
										setOpen(false);
									}}
									className={cx(
										ITEM,
										// Highlight follows the keyboard as well as the pointer,
										// which is what makes arrow keys legible.
										"data-[selected=true]:bg-brand-subtle data-[selected=true]:text-brand-ink",
										"data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-fg-subtle",
									)}
								>
									{/* The tick occupies a column whether or not it is shown,
									    so the labels do not shift when the choice moves. */}
									<span className="grid w-3.5 shrink-0 place-items-center text-brand-ink">
										{option.value === current && <Icon as={Check} size={14} />}
									</span>
									<span className="min-w-0 truncate">{option.label}</span>
								</Command.Item>
							))}
							{matches.length > shown.length && (
								<p className="px-2 py-1.5 text-[12px] text-fg-subtle">
									{t("ui.select.more", { count: matches.length - shown.length })}
								</p>
							)}
						</Command.List>
					</Command>
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}

/**
 * The options whose label contains the query, ignoring case and surrounding
 * space; all of them for an empty query. Exported for its test.
 */
export function matchOptions<T extends { label: string }>(options: T[], query: string): T[] {
	const needle = query.trim().toLocaleLowerCase();
	return needle === ""
		? options
		: options.filter((option) => option.label.toLocaleLowerCase().includes(needle));
}
